/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { Tab, Tabs } from '@mui/material';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import type { TafFromBackend, TimeSlot } from '../../../types';
import { useTafTranslation } from '../../../utils/i18n';
import { useTafConfigContext } from '../../TafWrapper/TafConfigWrapper';
import {
  calculateBaseTime,
  formatBaseTime,
  getBaseTime,
  muiTabA11yProps,
} from '../utils';

interface TimeSlotTab {
  title: string;
  timeSlot: TimeSlot;
}

interface TopTabsProps {
  timeSlot: TimeSlot;
  tafFromBackend?: TafFromBackend;
  onChangeTimeSlot?: (timeSlot: TimeSlot, isFormDirty: boolean) => void;
}

const TopTabs: React.FC<TopTabsProps> = ({
  timeSlot,
  tafFromBackend,
  onChangeTimeSlot = (): void => {},
}) => {
  const { t } = useTafTranslation();
  const {
    formState: { isDirty },
  } = useFormContext();

  const { tafConfig } = useTafConfigContext();

  const activeTab = timeSlot === 'UPCOMING' ? 0 : 1;
  const { currentBaseTime, nextBaseTime } = tafFromBackend
    ? getBaseTime(
        tafFromBackend.taf.baseTime,
        tafFromBackend.taf.location,
        tafConfig!,
      )
    : calculateBaseTime(tafConfig!);

  const tabs: TimeSlotTab[] = [
    {
      title: `${t('top-tabs-upcoming')} ${formatBaseTime(nextBaseTime)}`,
      timeSlot: 'UPCOMING',
    },
    {
      title: `${t('top-tabs-current')} ${formatBaseTime(currentBaseTime)}`,
      timeSlot: 'ACTIVE',
    },
  ];

  const onChangeTab = (
    _event: React.SyntheticEvent,
    newValue: number,
  ): void => {
    onChangeTimeSlot(tabs[newValue].timeSlot, isDirty);
  };

  return (
    <Tabs
      aria-label="timeslot-tabs"
      data-testid="timeslot-tabs"
      onChange={onChangeTab}
      value={activeTab}
      variant="fullWidth"
    >
      {tabs.map((tab, index) => (
        <Tab
          data-testid={`toptab-${index}`}
          key={tab.title}
          label={tab.title}
          sx={{ fontSize: 16 }}
          {...muiTabA11yProps(index)}
        />
      ))}
    </Tabs>
  );
};

export default TopTabs;
